'use client';

import { useId, type ReactNode } from 'react';
import { ExternalLink, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { helpDestinationUrl, type HelpDestination } from '@/lib/guidance/areas';
import { trackGuidanceEvent } from '@/lib/guidance';

/**
 * What a (?) says: a short title, the points, and optionally the Help Centre
 * article that goes further. The copy lives beside the feature that owns it, and
 * the article is a {@link HelpDestination} from the guidance registry, never a
 * pasted URL, so a renamed article is one edit and the liveness check sees it.
 */
export interface InfoKeyRow {
  /** The thing being decoded, drawn exactly as the page draws it (a badge). */
  mark: ReactNode;
  text: string;
}

/** A run of key rows under an optional heading, such as who the row is waiting on. */
export interface InfoKeyGroup {
  heading?: string;
  rows: readonly InfoKeyRow[];
}

export interface InfoHelp {
  /**
   * Published: this is the `info` property of `guidance-info-opened`, so a
   * rename strands every audit row that carried the old one. Kebab-case, named
   * for the control it explains (`discovery-in-progress`).
   */
  id: string;
  title: string;
  /** Prose, as bullets. Above the key when there is one. */
  points?: readonly string[];
  /**
   * A key: the marks the page shows, each beside what it means. For a control
   * whose whole difficulty is telling its badges apart, so the reader matches
   * what they see to what it says instead of translating a name. The marks are
   * the page's own components, never look-alikes, so a re-toned badge changes
   * here with it.
   */
  key?: readonly InfoKeyGroup[];
  article?: HelpDestination;
}

interface InfoButtonProps {
  help: InfoHelp;
  /** Which side of the (?) the panel opens on. Below by default. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /**
   * The (?) often sits inside something that reacts to a click (a collapsible
   * header, a table row, a sort control). Opening help must not also fire that.
   * On by default because a (?) that toggles its own section is the failure
   * that was found first.
   */
  stopPropagation?: boolean;
}

/**
 * A (?) that opens a short panel on press, for a control whose consequence a
 * first-time reader cannot infer (GUIDE-19, the E2 direction of GUIDE-7).
 *
 * **Placement is the whole value, so this component adds nothing to reason
 * about it: there is no "show everywhere" mode.** Spike A measured a 0.1% median
 * open rate for tooltips on every control. A (?) earns its place only where the
 * placement map names it (`info-button-placement-map.md` beside the plan).
 *
 * **The rules for where a (?) goes and what goes in it are `frontend/CLAUDE.md`
 * § "(?) info buttons", and `__tests__/info-help-registry.test.ts` enforces the
 * mechanical half.** Declare each help as an `InfoHelp` constant in a
 * `*-help.ts(x)` file beside the feature, never inline.
 *
 * Deliberate choices, each against the E2 prototype it grew from:
 *
 * - **A Popover, not a Tooltip.** A Tooltip's panel is `role="tooltip"` and
 *   cannot be tabbed into, so a link inside it is unreachable by keyboard.
 * - **Press to open, no hover.** Reading needs no held pointer, and nothing
 *   opens unasked while a solicitor works down a list.
 * - **A 24px hit box around a 14px glyph** (`icon-2xs`), so the target is
 *   comfortable without the glyph outgrowing the title it sits beside.
 * - **Colour through the button's own `hint` tone**, not `group` classes on the
 *   glyph, which the shipped button refuses.
 * - **The article link opens in a new tab** and is the only interactive thing in
 *   the panel, so `Tab` from the trigger lands on it.
 */
export function InfoButton({
  help,
  side = 'bottom',
  align = 'start',
  stopPropagation = true
}: InfoButtonProps) {
  const titleId = useId();
  const swallow = stopPropagation
    ? (e: { stopPropagation: () => void }) => e.stopPropagation()
    : undefined;

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) trackGuidanceEvent('guidance-info-opened', { info: help.id });
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-2xs"
          tone="hint"
          className="cursor-pointer"
          aria-label={`About ${help.title.toLowerCase()}`}
          onClick={swallow}
        >
          <HelpCircle className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        aria-labelledby={titleId}
        className={cn(
          'space-y-3 tracking-normal normal-case',
          help.key ? 'w-[29rem]' : 'w-80'
        )}
        onClick={swallow}
      >
        <p id={titleId} className="text-sm font-semibold">
          {help.title}
        </p>
        {help.points && help.points.length > 0 && (
          <ul className="text-muted-foreground marker:text-foreground-subtle list-disc space-y-1.5 pl-4 text-sm">
            {help.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        )}
        {help.key && (
          <dl className="grid grid-cols-[max-content_1fr] items-center gap-x-4 gap-y-2.5">
            {help.key.map((group, i) => (
              <div
                key={group.heading ?? group.rows[0]?.text}
                className="contents"
              >
                {group.heading && (
                  <dt
                    className={cn(
                      'text-foreground-subtle col-span-2 text-xs font-medium',
                      i > 0
                        ? 'border-border-subtle mt-2 border-t pt-3'
                        : 'pb-0.5'
                    )}
                  >
                    {group.heading}
                  </dt>
                )}
                {group.rows.map((row) => (
                  <div key={row.text} className="contents">
                    <dt className="flex items-center">{row.mark}</dt>
                    <dd className="text-foreground text-sm">{row.text}</dd>
                  </div>
                ))}
              </div>
            ))}
          </dl>
        )}
        {help.article && (
          <div className="border-border-subtle border-t pt-3">
            <a
              href={helpDestinationUrl(help.article)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center gap-1 text-sm underline-offset-4 hover:underline"
            >
              Read the guide
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * A title with its (?) straight after it. The (?) sits after the words rather
 * than beside the header's buttons, so it takes no width from them and the
 * buttons stay exactly where they are without guidance.
 */
export function TitleWithInfo({
  title,
  help
}: {
  title: string;
  help: InfoHelp | null;
}) {
  if (!help) return <>{title}</>;
  return (
    <span className="inline-flex items-center gap-1">
      {title}
      <InfoButton help={help} />
    </span>
  );
}
